import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface OpenGraphData {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  url?: string;
}

/** Matches index.html default description for crawlers and sharing. */
const DEFAULT_SITE_DESCRIPTION =
  'Hebern provides automatic encryption services that quickly integrate into your data streams. Too easy for AI to mess up, and platform agnostic.';

@Injectable({
  providedIn: 'root',
})
export class OpenGraphService {
  private readonly baseUrl = environment.frontendUrl.replace(/\/$/, '');
  // Prefer WebP; most platforms support it now.
  private readonly defaultImage = `${this.baseUrl}/assets/${encodeURIComponent('Social Media.webp')}`;
  private readonly defaultImageFallback = this.defaultImage;
  private readonly defaultType = 'website';
  private readonly siteName = 'Hebern';

  /**
   * Keys are the first path segment (empty string = home), same as JsonLdService.
   */
  private readonly routeMetadata: Record<string, OpenGraphData> = {
    '': {
      title: 'Hebern - Quick and easy data encryption, secure from AI.',
      description: DEFAULT_SITE_DESCRIPTION,
    },
    about: {
      title: 'About - Hebern',
      description:
        'Learn about Hebern and how we help you add encryption to your data streams quickly.',
    },
    'pitch-deck': {
      title: 'Pitch Deck - Hebern',
      description: "Review Hebern's pitch deck and product overview.",
    },
    signup: {
      title: 'Sign Up - Hebern',
      description: 'Create your Hebern account and get started.',
    },
    welcome: {
      title: 'Welcome to Hebern',
      description:
        'Welcome to Hebern. Get started with automatic encryption for your data streams.',
    },
    confirmation: {
      title: 'Email Confirmation - Hebern',
      description: 'Confirm your email address to complete your Hebern account setup.',
    },
    survey: {
      title: 'Survey - Hebern',
      description: 'Complete this short Hebern survey.',
    },
    unsubscribe: {
      title: 'Unsubscribe - Hebern',
      description: 'Manage your Hebern email preferences.',
    },
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
  ) {
    this.updateOpenGraphTags();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateOpenGraphTags();
      });
  }

  /** First URL segment after optional leading slash; empty for home. */
  private routeKey(): string {
    const path = this.router.url.split('?')[0].replace(/^\//, '');
    if (!path) {
      return '';
    }
    return path.split('/')[0] ?? '';
  }

  private updateOpenGraphTags(): void {
    const key = this.routeKey();
    const metadata = this.routeMetadata[key] || {};

    const ogData: Required<OpenGraphData> = {
      title: metadata.title || this.getDefaultTitle(),
      description: metadata.description || DEFAULT_SITE_DESCRIPTION,
      image: metadata.image || this.defaultImageFallback,
      type: metadata.type || this.defaultType,
      url: metadata.url || `${this.baseUrl}${this.router.url.split('?')[0]}`,
    };

    this.setMetaTag('og:title', ogData.title);
    this.setMetaTag('og:description', ogData.description);
    const imageUrl =
      ogData.image === this.defaultImageFallback ? this.defaultImage : ogData.image;
    this.setMetaTag('og:image', imageUrl);
    this.setMetaTag('og:type', ogData.type);
    this.setMetaTag('og:url', ogData.url);
    this.setMetaTag('og:site_name', this.siteName);
    this.setMetaTag('og:locale', 'en_US');
    if (imageUrl.endsWith('.webp')) {
      this.setMetaTag('og:image:type', 'image/webp');
    }

    this.setMetaTag('twitter:card', 'summary_large_image');
    this.setMetaTag('twitter:title', ogData.title);
    this.setMetaTag('twitter:description', ogData.description);
    this.setMetaTag('twitter:image', imageUrl);
  }

  private setMetaTag(property: string, content: string): void {
    const isOpenGraph = property.startsWith('og:');
    const attribute = isOpenGraph ? 'property' : 'name';

    let element = this.document.querySelector(
      `meta[${attribute}="${property}"]`,
    ) as HTMLMetaElement;

    if (element) {
      element.setAttribute('content', content);
    } else {
      element = this.document.createElement('meta');
      element.setAttribute(attribute, property);
      element.setAttribute('content', content);
      this.document.head.appendChild(element);
    }
  }

  private getDefaultTitle(): string {
    const path = this.router.url.split('?')[0];
    if (path === '/' || path === '') {
      return this.routeMetadata[''].title ?? 'Hebern - Automatic Encryption Services';
    }
    const first = path.replace(/^\//, '').split('/')[0] ?? '';
    const words = first.split('-');
    const capitalized = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${capitalized} - Hebern`;
  }

  public updateOpenGraph(data?: OpenGraphData): void {
    if (data) {
      const key = this.routeKey();
      this.routeMetadata[key] = {
        ...this.routeMetadata[key],
        ...data,
      };
    }
    this.updateOpenGraphTags();
  }
}
